/**
 * App version control — drives the in-app update popup.
 *
 * Writes the single document `config/appVersion` that the mobile app reads on
 * launch (see Eat2Live app/util/UpdateCheck.js). Nothing here talks to the
 * stores: publishing a build to Play or the App Store does not prompt anybody
 * until this document says a newer version exists.
 *
 * IMPORTANT — this writes to db1 (eat4living-3f595), the project the mobile app
 * actually reads through @react-native-firebase. The default `db` in this admin
 * points at pocdoc-148bb, and saving there would silently do nothing.
 *
 * Android and iOS carry different version numbers (1.1.4 vs 2.2.8 at the time of
 * writing) and each side is compared only against its own, so the two halves of
 * this form are not interchangeable.
 */

import React, { useState, useEffect } from 'react';
import '../App.css';
import { useNavigate } from 'react-router-dom';
import { Form, FormGroup, Button, Label, Input } from 'reactstrap';
import { Oval as Loader } from 'react-loader-spinner';
import { ToastContainer } from 'react-toastify';
import MyStorage from './util/storage';
import showToast from './util/toast';
import { db1 } from '../../firebase/firebase1';

const COLLECTION = 'config';
const DOC = 'appVersion';

const EMPTY = { latestVersion: '', minimumVersion: '', storeUrl: '' };

/**
 * Accepts a dotted numeric version, or blank.
 *
 * Blank is meaningful rather than invalid: it is how a field is switched off.
 * Anything the app cannot parse is treated by it as "no update", so a typo here
 * fails silently on the device — which is exactly why it is rejected up front.
 */
const isValidVersion = (v) => v.trim() === '' || /^\d+(\.\d+)*$/.test(v.trim());

const AdminAppVersion = () => {
  const navigate = useNavigate();

  const [android, setAndroid] = useState(EMPTY);
  const [ios, setIos] = useState(EMPTY);
  const [loader, setLoader] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    db1.collection(COLLECTION)
      .doc(DOC)
      .get()
      .then((snap) => {
        const data = snap.exists ? snap.data() || {} : {};
        setAndroid({ ...EMPTY, ...(data.android || {}) });
        setIos({ ...EMPTY, ...(data.ios || {}) });
        setLoader(false);
      })
      .catch((e) => {
        console.log('load appVersion failed: ' + e);
        showToast('error', 'Could not load current version config');
        setLoader(false);
      });
  }, []);

  if (!MyStorage.isLogin()) {
    navigate('/admin/login', { replace: true });
    return null;
  }

  const save = () => {
    const fields = [
      ['Android latest', android.latestVersion],
      ['Android minimum', android.minimumVersion],
      ['iOS latest', ios.latestVersion],
      ['iOS minimum', ios.minimumVersion],
    ];
    const bad = fields.find(([, v]) => !isValidVersion(v));
    if (bad) {
      showToast('error', `${bad[0]} is not a valid version (use 1.2.3, or leave blank)`);
      return;
    }

    setSaving(true);
    const clean = (v) => ({
      latestVersion: v.latestVersion.trim(),
      minimumVersion: v.minimumVersion.trim(),
      storeUrl: v.storeUrl.trim(),
    });

    // merge:false — the document holds only these two keys, and a merge would
    // leave a stale platform behind if one is ever renamed.
    db1.collection(COLLECTION)
      .doc(DOC)
      .set({ android: clean(android), ios: clean(ios) })
      .then(() => {
        setSaving(false);
        showToast('success', 'Saved. Users are prompted on their next launch.');
      })
      .catch((e) => {
        console.log('save appVersion failed: ' + e);
        setSaving(false);
        showToast('error', 'Save failed: ' + e.message);
      });
  };

  const platformBlock = (title, value, setValue, hint) => (
    <div style={{ flex: 1, minWidth: 300, marginRight: 30 }}>
      <h5 style={{ marginBottom: 4 }}>{title}</h5>
      <div style={{ fontSize: 12, color: 'gray', marginBottom: 12 }}>{hint}</div>

      <FormGroup>
        <Label>Latest version</Label>
        <Input
          value={value.latestVersion}
          placeholder="e.g. 1.1.5"
          onChange={(e) => setValue({ ...value, latestVersion: e.target.value })}
        />
        <div style={{ fontSize: 12, color: 'gray' }}>
          Users below this see a dismissible "Update Available" prompt, once a day.
        </div>
      </FormGroup>

      <FormGroup>
        <Label>Minimum version</Label>
        <Input
          value={value.minimumVersion}
          placeholder="e.g. 1.1.0"
          onChange={(e) => setValue({ ...value, minimumVersion: e.target.value })}
        />
        <div style={{ fontSize: 12, color: 'gray' }}>
          Users below this see a stronger "Update Required" prompt, twice a day. Still
          dismissible. Leave blank to never force.
        </div>
      </FormGroup>

      <FormGroup>
        <Label>Store URL (optional)</Label>
        <Input
          value={value.storeUrl}
          placeholder="leave blank to use the default store listing"
          onChange={(e) => setValue({ ...value, storeUrl: e.target.value })}
        />
      </FormGroup>
    </div>
  );

  return (
    <div style={{ padding: 20, maxWidth: 900 }}>
      <h3>App Version</h3>
      <div style={{ fontSize: 13, color: 'gray', marginBottom: 6 }}>
        Controls the update popup inside the mobile app. Publishing to the stores does
        not prompt anyone on its own — set the version here once the new build is
        actually live.
      </div>
      <div style={{ fontSize: 13, color: '#B40404', marginBottom: 20 }}>
        Android and iOS use different numbering. Enter each platform's own version —
        copying one into the other stops that platform prompting at all.
      </div>

      {loader ? (
        <Loader color="#2CBE7B" height={40} width={40} />
      ) : (
        <Form>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {platformBlock('Android', android, setAndroid, 'versionName in build.gradle')}
            {platformBlock('iOS', ios, setIos, 'MARKETING_VERSION in Xcode')}
          </div>

          <Button
            style={{ marginTop: 10, backgroundColor: '#2CBE7B', borderColor: '#2CBE7B' }}
            disabled={saving}
            onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Form>
      )}

      <ToastContainer />
    </div>
  );
};

export default AdminAppVersion;
